/**
 * BLRRD Pointwise Filters — Solarize
 *
 * Inversion above a threshold. Standard photo-editing math:
 *
 *   out = (in < threshold) ? in : 1 - in
 *
 * Models the Sabatier effect (1862) — overexposed silver halide
 * partially re-developing as a negative. Photoshop calls it Solarize
 * (Image > Adjustments). Per-channel mode applies the test
 * independently per R/G/B; per-luminance mode (default) uses Rec.709
 * Y as the gating signal but inverts each channel by 1−v.
 *
 * Pipeline slot: POST-VIEW-TRANSFORM. The 1-v inversion is only
 * meaningful in display-referred [0,1] — on scene-linear the
 * highlight range is unbounded and the result would clip immediately.
 *
 * Identity short-circuit: threshold >= 1 → no pixel gets inverted →
 * passthrough.
 */

/** Default Solarize params. */
export function defaultSolarizeParams() {
    return { threshold: 0.5, perChannel: false };
}

/** True iff `params` represents a no-op. */
export function isIdentitySolarize(params) {
    if (!params) return true;
    const t = params.threshold;
    if (typeof t !== 'number') return true;
    return t >= 1;
}

/**
 * Apply Solarize to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { threshold, perChannel? }
 * @param {object}       [opts]      { selectionMask? }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applySolarize(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentitySolarize(params)) return rgba;
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applySolarize: selectionMask length mismatch');
    }

    const t = params.threshold;
    const N = w * h;
    const perChannel = params.perChannel === true;

    if (perChannel) {
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            const newR = rgba[i]     >= t ? 1 - rgba[i]     : rgba[i];
            const newG = rgba[i + 1] >= t ? 1 - rgba[i + 1] : rgba[i + 1];
            const newB = rgba[i + 2] >= t ? 1 - rgba[i + 2] : rgba[i + 2];
            applyMix(rgba, i, m, newR, newG, newB);
        }
    } else {
        /* Luminance-gated: invert when Y >= threshold, else passthrough. */
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
            const Y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            if (Y >= t) {
                applyMix(rgba, i, m, 1 - r, 1 - g, 1 - b);
            }
            /* else passthrough — no write needed. */
        }
    }
    return rgba;
}

function applyMix(rgba, i, m, r, g, b) {
    if (m >= 1) {
        rgba[i] = r;
        rgba[i + 1] = g;
        rgba[i + 2] = b;
    } else {
        const inv = 1 - m;
        rgba[i]     = inv * rgba[i]     + m * r;
        rgba[i + 1] = inv * rgba[i + 1] + m * g;
        rgba[i + 2] = inv * rgba[i + 2] + m * b;
    }
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applySolarize: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applySolarize: rgba.length !== w*h*4');
    }
}
